import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings, UserPlus, Trash2, ShieldCheck, User as UserIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUsers, createUser, deleteUser, changeCredentials } from "@/lib/api";

const inputCls =
  "w-full bg-black border border-zinc-700 px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:border-amber-500";
const labelCls = "text-[11px] uppercase tracking-widest text-zinc-500";
const btnCls =
  "inline-flex items-center justify-center gap-2 border border-amber-500/60 bg-amber-950/20 px-3 py-2 text-xs uppercase tracking-widest text-amber-term hover:bg-amber-900/30 disabled:opacity-50 transition-colors";

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function AccountSection() {
  const { user, updateSession } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error("Enter your current password to confirm");
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (email && email.toLowerCase() !== user.email) payload.new_email = email.toLowerCase();
      if (name && name !== user.name) payload.new_name = name;
      if (newPassword) payload.new_password = newPassword;
      const data = await changeCredentials(payload);
      updateSession(data);
      setNewPassword("");
      setCurrentPassword("");
      toast.success("Account updated");
    } catch (e2) {
      toast.error(errMsg(e2, "Could not update account"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="account-section">
      <div className="space-y-1.5">
        <label className={labelCls}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="account-email" />
      </div>
      <div className="space-y-1.5">
        <label className={labelCls}>Display Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="account-name" />
      </div>
      <div className="space-y-1.5">
        <label className={labelCls}>New Password (leave blank to keep)</label>
        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="account-new-password" />
      </div>
      <div className="space-y-1.5 border-t border-zinc-800 pt-3">
        <label className={labelCls}>Current Password (required to save)</label>
        <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="account-current-password" />
      </div>
      <button type="submit" disabled={saving} className={`${btnCls} w-full`} data-testid="account-save">
        <KeyRound size={13} strokeWidth={1.5} /> {saving ? "Saving…" : "Save Changes"}
      </button>
    </form>
  );
}

function UsersSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["users"], queryFn: fetchUsers });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [adding, setAdding] = useState(false);

  const users = q.data?.users || [];

  const add = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Email and initial password are required");
    setAdding(true);
    try {
      await createUser({ email: email.toLowerCase(), password, name: name || undefined });
      setEmail(""); setName(""); setPassword("");
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("User added");
    } catch (e2) {
      toast.error(errMsg(e2, "Could not add user"));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (em) => {
    try {
      await deleteUser(em);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Removed ${em}`);
    } catch (e2) {
      toast.error(errMsg(e2, "Could not remove user"));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-section">
      <div className="space-y-1.5">
        <span className={labelCls}>Authorized users ({users.length})</span>
        <div className="border border-zinc-800 divide-y divide-zinc-900 max-h-[180px] overflow-auto">
          {users.map((u) => (
            <div key={u.email} className="flex items-center justify-between px-3 py-2" data-testid={`user-row-${u.email}`}>
              <div className="flex items-center gap-2 min-w-0">
                {u.role === "admin" ? (
                  <ShieldCheck size={13} className="text-amber-term shrink-0" />
                ) : (
                  <UserIcon size={13} className="text-zinc-500 shrink-0" />
                )}
                <span className="text-xs text-zinc-200 truncate">{u.email}</span>
                <span className="text-[11px] uppercase tracking-widest text-zinc-600">{u.role}</span>
              </div>
              {u.email !== user.email && (
                <button
                  onClick={() => remove(u.email)}
                  className="text-zinc-500 hover:text-neg transition-colors"
                  title="Remove user"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 size={13} strokeWidth={1.5} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={add} className="space-y-2 border-t border-zinc-800 pt-3" data-testid="add-user-form">
        <span className={labelCls}>Add a user</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="email@example.com" data-testid="new-user-email" />
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Display name (optional)" data-testid="new-user-name" />
        <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="Initial password (share with them)" data-testid="new-user-password" />
        <button type="submit" disabled={adding} className={`${btnCls} w-full`} data-testid="add-user-submit">
          <UserPlus size={13} strokeWidth={1.5} /> {adding ? "Adding…" : "Add User"}
        </button>
        <p className="text-[11px] text-zinc-600 leading-snug">
          They sign in with this email + password, then can change their own password from Account.
        </p>
      </form>
    </div>
  );
}

export default function AccountPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("account");
  const isAdmin = user?.role === "admin";

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-term transition-colors"
          title="Account & users"
          data-testid="open-account-panel"
        >
          <Settings size={12} strokeWidth={1.5} />
          <span className="hidden sm:inline">ACCOUNT</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-black border-zinc-700 text-zinc-100" data-testid="account-panel">
        <DialogHeader>
          <DialogTitle className="text-amber-term font-mono text-base uppercase tracking-wider">
            Account & Users
          </DialogTitle>
          <DialogDescription className="sr-only">
            Manage your account credentials and authorized users.
          </DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <div className="flex border border-zinc-800 text-[11px] uppercase tracking-widest">
            <button
              onClick={() => setTab("account")}
              className={`flex-1 py-2 ${tab === "account" ? "bg-amber-950/30 text-amber-term" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid="tab-account"
            >
              My Account
            </button>
            <button
              onClick={() => setTab("users")}
              className={`flex-1 py-2 border-l border-zinc-800 ${tab === "users" ? "bg-amber-950/30 text-amber-term" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid="tab-users"
            >
              Manage Users
            </button>
          </div>
        )}

        {tab === "account" || !isAdmin ? <AccountSection /> : <UsersSection />}
      </DialogContent>
    </Dialog>
  );
}
